<template>
  <div class="relative -mx-5 h-97dvh">
    <MobileTimelineHeader
      :resources="resources"
      :active-resource="activeResource"
    />
    <div class="relative border-t pt-4 h-full">
      <div ref="eventCalendarRef"></div>
    </div>
    <div class="fixed bottom-12 right-4 z-10 flex gap-3">
      <UnassignedEventsFOB />
      <CreateNewFOB />
    </div>
    <div>
      <TimelineFooter :last-load="lastLoad" />
    </div>
    <AppointmentDetailsDrawer
      v-if="selectedAppointmentId"
      :appointment-id="selectedAppointmentId"
      @hide-drawer="onHideDrawer"
      @appointment-updated="onAppointmentUpdated"
    />
    <CalendarEventModal
      v-if="selectedEventId"
      class="hidden md:block"
      :calendar-event-id="selectedEventId || ''"
      auto-open
      @close="closeEventModal"
    ></CalendarEventModal>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { getComponentHtml } from "@/lib/getComponentHTML";
import { getQueryVariables } from "@/lib/url";

import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { AppointmentV2 } from "@/typings/appointment";
import { MbscCalendarEventData } from "@mobiscroll/javascript";
import AppointmentEvent from "./AppointmentEvent.vue";
import CreateNewFOB from "./CreateNewFOB.vue";
import MobileTimelineHeader from "./MobileTimelineHeader.vue";
import TimelineFooter from "./TimelineFooter.vue";
import UnassignedEventsFOB from "./UnassignedEventsFOB.vue";

const {
  initCalendar,
  destroyCalendar,
  setCalendarDate,
  resources,
  activeResource,
  selectedAppointmentId,
  selectedEventId,
  setStartParameter,
  setTimezoneLabel,
  handleEventRightClick,
  handleEventClick
} = useEventCalendar();
const props = defineProps<{ date: Date }>();
const eventCalendarRef = ref<HTMLElement | null>(null);
const lastLoad = ref(new Date());

onMounted(() => {
  initCalendar(eventCalendarRef.value, props.date, {
    renderScheduleEvent: (data: MbscCalendarEventData) =>
      getComponentHtml(AppointmentEvent, { event: data }),
    renderResource: () => "",
    renderHeader: () => "",
    renderDay: () => "",
    onEventClick: handleEventClick,
    onEventRightClick: handleEventRightClick,
    onPageLoaded: handlePageLoad
  });
  // Auto-open appointment details drawer if appointment_id query param is present
  const params = getQueryVariables();
  if (params.appointment_id) {
    selectedAppointmentId.value = params.appointment_id;
  }
});

onBeforeUnmount(() => {
  destroyCalendar();
});

watch(
  () => props.date,
  (newVal) => {
    debouncedDateChange(newVal);
  }
);

const closeEventModal = () => {
  selectedEventId.value = "";
};
const handlePageLoad = () => {
  setStartParameter(props.date);
  setTimeout(() => (lastLoad.value = new Date()), 100);
  setTimezoneLabel();
};
const onAppointmentUpdated = (appointment: AppointmentV2) => {
  // Navigate to selected appointment on scheduler
  setCalendarDate(appointment.arrival_window_start);
};
const debouncedDateChange = debounce(
  (date: Date) => setCalendarDate(date),
  750
);
const onHideDrawer = () => (selectedAppointmentId.value = "");
</script>

<style lang="scss">
.mbsc-schedule-header,
.mbsc-schedule-all-day-cont {
  display: none;
}
.mbsc-schedule-time-indicator-time.mbsc-schedule-time-indicator-time-y {
  background-color: #2563eb; /* A nice shade of blue */
  color: white; /* Sets the text color to white */
  padding: 2px 6px; /* Adds some vertical and horizontal spacing */
  border-radius: 4px; /* Rounds the corners of the container */
}
/*
  This targets the pseudo-element that creates the horizontal line
  and centers it within its container.
*/
.mbsc-schedule-time-indicator-y::after {
  left: 50% !important;
  transform: translateX(-50%) !important;
}

/*
  This re-centers the time label directly above the
  now-centered line.
*/
.mbsc-schedule-time-indicator-time-y {
  left: 50% !important;
  transform: translateX(-42%) !important;
}
</style>
